import {
  GetStaticPathsResult,
  GetStaticPropsContext,
  GetStaticPropsResult,
} from "next";
import { genderTranslations } from "src/utils/routeTranslations";
import { PromotionContextProvider } from "src/contexts/promotion.context";
import { BrandView as NewBrandView } from "src/organisms/BrandView";
import getConfig from "next/config";
import { BRANDS, PRODUCTS } from "src/utils/algolia";
import { AlgoliaSearchBrand } from "src/types/Algolia";
import { InstantSearchServerState } from "react-instantsearch-core-v7";
import { capitalize } from "instantsearch.js/es/lib/utils";
import { getNavigationItems } from "services/graphql/get-navigation-items";
import { NavigationItems, ShopType } from "src/types/graphql.d";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { getAlgoliaProps } from "src/utils/algolia/get-algolia-props";

const brandTranslations = [
  "marques",
  "marken",
  "merken",
  "marki",
  "marcas",
  "marchi",
  "brands",
  "maerker",
  "varumarken",
];

type BrandPageProps = {
  gender: string;
  slug: string;
  title: string;
  categoryPageId: string;
  indexName: string;
  locale: string;
  breadcrumb: { title: string; slug?: string }[];
  navigationItems?: NavigationItems | null;
  search: {
    appId: string;
    searchKey: string;
  };
  brand: AlgoliaSearchBrand;
  serverState?: InstantSearchServerState;
};

function BrandPage({
  pageProps: {
    indexName,
    locale,
    search,
    categoryPageId,
    gender,
    serverState,
    slug,
    title,
    brand,
  },
}: {
  pageProps: BrandPageProps;
}): JSX.Element {
  return (
    <PromotionContextProvider>
      <NewBrandView
        categoryPageId={categoryPageId}
        gender={gender}
        indexName={indexName}
        locale={locale}
        search={search}
        slug={slug || ""}
        title={title}
        serverState={serverState}
        brand={brand}
      />
    </PromotionContextProvider>
  );
}

export function getStaticPaths(): GetStaticPathsResult {
  return {
    paths: [],
    fallback: "blocking",
  };
}

const { serverRuntimeConfig } = getConfig();

export async function getStaticProps(
  context: GetStaticPropsContext<{
    slug: string;
    brandOrCategoriesSlug: string;
    categories: string[];
  }>
): Promise<GetStaticPropsResult<BrandPageProps>> {
  if (
    !context.params ||
    !context.params.slug ||
    !context.params.brandOrCategoriesSlug ||
    !context.params.categories ||
    !brandTranslations.includes(context.params?.slug || "")
  ) {
    return {
      notFound: true,
    };
  }

  const gender = context.params.categories[0];
  const isValidGender = genderTranslations.includes(gender);

  if (!isValidGender) {
    return {
      notFound: true,
    };
  }

  const { hits, getServerState } = await getAlgoliaProps<AlgoliaSearchBrand>({
    index: `${serverRuntimeConfig.LOCALE}_${BRANDS}`,
    filters: `slug: ${context.params.brandOrCategoriesSlug}`,
    gender,
  });

  const hit = hits.find(
    (hit) => hit.slug === context.params?.brandOrCategoriesSlug
  );

  if (!hit) {
    return {
      notFound: true,
    };
  }

  const title = hit.name;
  const categoryPageId = `${capitalize(gender)} > ${hit.name}`;
  const breadcrumb: { title: string; slug?: string }[] = [
    { title: "Home", slug: `/${context.params?.slug || ""}` },
    { title: "Categories", slug: "/categories" },
    { title: hit.name },
  ];

  const { navigationItems } =
    (await getNavigationItems(localeFix({ gender }) as ShopType)) || [];

  const serverState = await getServerState({
    pageType: "BRAND_PLP",
    categoryPageId,
  });

  // TEMP: We will revalidate the cache every 15min temporally.
  // Once we have backend support we will revalidate on demand.
  return {
    revalidate: 60 * 15,
    props: {
      locale: serverRuntimeConfig.LOCALE,
      gender,
      slug: context.params?.brandOrCategoriesSlug,
      title,
      categoryPageId,
      indexName: `${serverRuntimeConfig.LOCALE}_${PRODUCTS}`,
      breadcrumb,
      navigationItems,
      search: {
        appId: process.env.ALGOLIA_APP_ID,
        searchKey: process.env.ALGOLIA_SEARCH_API_KEY,
      },
      brand: hit,
      serverState,
    },
  };
}
export default BrandPage;
